<template>
  <!--BOOST LANDING PAGE: Story CD-18 -->
  <PageHeader title="Boost Rewards" v-model="selectedState" :options="stateOptions" placeholder="All Savings plan" />
  <div class="w-full flex items-center justify-center" v-if="isLoading">
    <CustomSkeleton class="rounded-full" />
  </div>
  <div v-else-if="boostOrders.length > 0" :class="{ 'pb-[70px] px-2': !isDesktop }">
    <div class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grip-cols-4 mb-4">
      <BoostRewardCard v-for="(order, index) in boostOrders" :order="order" :key="index" />
    </div>

    <PaginationControls
      :total-count="totalCount"
      :current-page="pageNumber"
      :page-size="generalStore.paymentPlanPageSize"
      :page-size-options="generalStore.getPaymentPlanPageSizeOptions()"
      @update:page="onUpdatePage"
      @update:pageSize="onUpdatePageSize"
    />
  </div>
  <NoActiveOrders
    v-else
    :title="'No Savings Plan Found'"
    :description="'Start a savings plan and view it here'"
    variant="boost"
  />
</template>

<script lang="ts" setup>
import CustomSkeleton from '@/components/common/CustomSkeleton.vue';
import NoActiveOrders from '@/components/common/NoActiveOrders.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
import { NetworkError } from '@/errors';
import type { OrderServiceGetManyRequest } from '@/models/orders';
import { fetchOrders } from '@/services/orders';
import { useGeneralStore } from '@/stores/general';
import { useOrderStore } from '@/stores/orders';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { computed, ref, watch } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';
import BoostRewardCard from './BoostRewardCard.vue';

const generalStore = useGeneralStore();
const orderStore = useOrderStore();
const { isDesktop } = useBreakpoint();

const boostOrders = ref([] as ModelOrderResponse[]);
const pageNumber = ref(1);
const isLoading = ref(false);
const selectedState = ref('all');
const totalCount = ref(0);
const useStoreData = ref(true);
const orderParams = ref<OrderServiceGetManyRequest>({
  populate: ['voucher', 'plans', 'plans.benefactor'],
  type: 'REWARDS',
});

const storeBoostOrders = computed(() => orderStore.allOrders.filter((o) => o.type === 'REWARDS'));

const stateOptions = [
  { value: 'all', label: 'All Savings plan' },
  { value: 'PLACED', label: 'Active savings plans' },
  { value: 'CANCELLED', label: 'Cancelled savings plans' },
  { value: 'COMPLETED', label: 'Completed savings plans' },
  { value: 'PARTIAL', label: 'Partial savings plans' },
];

const getBoostRewards = async () => {
  isLoading.value = true;
  try {
    orderParams.value.limit = generalStore.paymentPlanPageSize;
    orderParams.value.skip = (pageNumber.value - 1) * generalStore.paymentPlanPageSize;

    if (selectedState.value !== 'all') {
      orderParams.value.state = selectedState.value as any;
    } else {
      delete orderParams.value.state;
    }

    const { orders, totalCount: count } = await fetchOrders(orderParams.value);

    boostOrders.value = orders || [];
    totalCount.value = count || 0;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('shopper-boost-rewards', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('shopper-boost-rewards', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

watch(selectedState, () => {
  useStoreData.value = false;
  pageNumber.value = 1;
  getBoostRewards();
});

const onUpdatePage = (page: number) => {
  useStoreData.value = false;
  pageNumber.value = page;
  getBoostRewards();
};

const onUpdatePageSize = (size: number) => {
  useStoreData.value = false;
  generalStore.paymentPlanPageSize = size;
  pageNumber.value = 1;
  getBoostRewards();
};

const init = async () => {
  if (orderStore.allOrdersFetched) {
    boostOrders.value = storeBoostOrders.value;
    totalCount.value = storeBoostOrders.value.length;
  } else {
    await getBoostRewards();
  }
};

init();
</script>
